import { z } from "zod";
import { sessionStore, useStore } from "../../lib/store";
import { useEffect } from "react";
import { apiClient } from "../../services/api";
import { useCurrentUser } from "../user/hooks/useCurrentUser";

/**
 * Notification ids whose interaction still has to be reported to the api.
 *
 * Deferred rather than fired inline because the endpoints are private and a
 * notification tap frequently lands BEFORE auth has resolved — that is the whole
 * point of this store.
 *
 * `sessionStore`, not `memoryStore`: a push open is usually a COLD open, and the
 * webview can reload between `history.replace` to the target and the flush
 * (iOS discards in-memory state aggressively). With an in-memory store those
 * opens were silently dropped, which under-counted every push metric.
 *
 * Two separate queues because they are two different metrics on the api side —
 * `pushOpens` (the user tapped the notification banner) and `clicks` (a tap
 * inside the in-app notification center).
 */
const pendingSchema = z.array(z.string());

export const pendingPushOpenStore = sessionStore<string[]>(
  "pending-push-opens",
  [],
  pendingSchema
);

export const pendingBroadcastClickStore = sessionStore<string[]>(
  "pending-broadcast-clicks",
  [],
  pendingSchema
);

/**
 * Flush both queues once a user is available. Mounted once, in `AppLayout`.
 *
 * Each id is removed from its queue BEFORE the request goes out: a failed ack is
 * dropped, never retried in a loop. An analytics ack must not be able to wedge
 * the app or spam the api on a persistent failure.
 */
export function usePushOpenTracking() {
  const { currentUser } = useCurrentUser();
  const [pendingPushOpens, setPendingPushOpens] =
    useStore(pendingPushOpenStore);
  const [pendingBroadcastClicks, setPendingBroadcastClicks] = useStore(
    pendingBroadcastClickStore
  );

  useEffect(() => {
    if (currentUser === null) return;
    if (pendingPushOpens.length === 0) return;

    const ids = pendingPushOpens;
    setPendingPushOpens([]);
    for (const id of ids) {
      void apiClient
        .post("/webapp/private/notification/:id/push-opened", {
          param: { id },
        })
        .catch(console.error);
    }
  }, [currentUser, pendingPushOpens, setPendingPushOpens]);

  useEffect(() => {
    if (currentUser === null) return;
    if (pendingBroadcastClicks.length === 0) return;

    const ids = pendingBroadcastClicks;
    setPendingBroadcastClicks([]);
    for (const id of ids) {
      void apiClient
        .post("/webapp/private/notification/broadcasts/:id/clicked", {
          param: { id },
        })
        .catch(console.error);
    }
  }, [currentUser, pendingBroadcastClicks, setPendingBroadcastClicks]);
}
